import React, { useState, useEffect, useRef } from 'react';
import { Form, Row, Col, Input, Button } from 'reactstrap';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { ModalButtonBuilder, RegisteredInputsBuilder, ToastBuilder, ButtonBuilder, ApiRequest } from '../../../../../../../../../GlobalComponents/Builders';
import { parseJSONSafe, buildSelectOptionsFromArray } from '../../../../../../../../../GlobalComponents/Helpers';
import CreatableMultiSelect from '../../../../../../../../../GlobalComponents/Common/Component/CreatableMultiSelect';
import { useForm } from 'react-hook-form';

const OPERATORS_BY_TYPE = {
    string:  [{ value: '=', label: '=' }, { value: '!=', label: '!=' }, { value: 'like', label: 'like' }, { value: 'contains', label: 'contains' }, { value: 'in', label: 'in' }, { value: 'not in', label: 'not in' }],
    boolean: [{ value: '=', label: '=' }],
    integer: [{ value: '=', label: '=' }, { value: '!=', label: '!=' }, { value: '>', label: '>' }, { value: '>=', label: '>=' }, { value: '<', label: '<' }, { value: '<=', label: '<=' }, { value: 'in', label: 'in' }, { value: 'not in', label: 'not in' }],
    date:    [{ value: '=', label: '=' }, { value: '!=', label: '!=' }, { value: '>', label: '>' }, { value: '>=', label: '>=' }, { value: '<', label: '<' }, { value: '<=', label: '<=' }],
};

const BOOLEAN_OPTIONS = [{ value: 'true', label: 'True' }, { value: 'false', label: 'False' }];

const FilterBuilder = ({ getCurrentFilter, onApply, readOnly, jobResourceMappings, sourceResourceId, jobId }) => {

    const { register, handleSubmit, errors = {}, control, setValue } = useForm();
    const [integrationFilters, setIntegrationFilters] = useState([]);
    const [globalFilters, setGlobalFilters] = useState([]);
    const [apiArgs, setApiArgs] = useState([]);
    const [fields, setFields] = useState('');
    const [options, setOptions] = useState([]);
    const [conditionsKey, setConditionsKey] = useState('conditions');
    const [isStringFilter, setIsStringFilter] = useState(false);
    const [filterableKeys, setFilterableKeys] = useState([]);
    const [filterableKeysLoaded, setFilterableKeysLoaded] = useState(false);
    const filterableKeysCache = useRef({});

    const conditionsKeyOptions = ['conditions', 'searchFilters', 'globalFilters'];
    const conditionsKeySelectOptions = conditionsKeyOptions.map(k => ({ label: k, value: k }));
    const opOptions = [
        { label: 'in', value: 'in' }, { label: 'not in', value: 'not in' },
        { label: 'equals', value: 'equals' }, { label: 'not_equals', value: 'not_equals' },
        { label: 'contains', value: 'contains' }, { label: 'not_contains', value: 'not_contains' },
        { label: 'array_contains', value: 'array_contains' }, { label: 'array_not_contains', value: 'array_not_contains' },
    ];

    // Operators that filter against a static/manual value list rather than a referenced source resource
    const STATIC_VALUE_OPS = ['array_contains', 'array_not_contains'];
    const globalFilterOpOptions = [
        { label: '=', value: '=' }, { label: '!=', value: '!=' },
        { label: '>', value: '>' }, { label: '<', value: '<' },
        { label: '>=', value: '>=' }, { label: '<=', value: '<=' },
        { label: 'in', value: 'in' }, { label: 'not in', value: 'not in' },
        { label: 'like', value: 'like' }, { label: 'contains', value: 'contains' },
    ];

    const selectStyles = {
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        // Menu portals to document.body so the .react-select-sm wrapper can't reach it —
        // size the options here so the dropdown list matches the compact controls.
        menuList: (base) => ({ ...base, fontSize: 12 }),
        option: (base) => ({ ...base, fontSize: 12, padding: '4px 8px' }),
    };

    const resourceOptions = (jobResourceMappings?.current || []).map(m => ({
        label: `${m.source_resource_name} (${m.source_resource_id})`,
        value: m.source_resource_id,
    }));

    // Filterable keys for the current source resource
    const resourceKeys = filterableKeys.filter(k => String(k.resource_id) === String(sourceResourceId));
    // Show the human label alongside the actual API field name when they differ, so users
    // can see exactly which field the filter targets (e.g. "Status Name (status/name)").
    const keyOptions = resourceKeys.map(k => ({
        value: k.key_name,
        label: (k.label && k.label !== k.key_name) ? `${k.label} (${k.key_name})` : k.key_name,
    }));
    const hasKeys = keyOptions.length > 0;

    // Get operators for a specific field based on its data_type
    const getOperatorsForField = (fieldName) => {
        const keyMeta = resourceKeys.find(k => k.key_name === fieldName);
        if (keyMeta && OPERATORS_BY_TYPE[keyMeta.data_type]) {
            return OPERATORS_BY_TYPE[keyMeta.data_type];
        }
        return globalFilterOpOptions;
    };

    const getKeyMeta = (fieldName) => resourceKeys.find(k => k.key_name === fieldName) || null;

    const loadFilterableKeys = () => {
        if (!jobId) return;
        // Use cache if already loaded for this job
        if (filterableKeysCache.current[jobId]) {
            setFilterableKeys(filterableKeysCache.current[jobId]);
            setFilterableKeysLoaded(true);
            return;
        }
        ApiRequest('GET', '/dmt/jobs/{jobId}/filterableKeys', {
            pathParams: { jobId: String(jobId) },
        }).then(res => {
            const keys = res.data?.keys || [];
            filterableKeysCache.current[jobId] = keys;
            setFilterableKeys(keys);
            setFilterableKeysLoaded(true);
        }).catch(() => setFilterableKeysLoaded(true));
    };

    const parseCurrentFilter = () => {
        try {
            const current = typeof getCurrentFilter === 'function' ? getCurrentFilter() : getCurrentFilter;
            if (!current) return {};
            const cleaned = typeof current === 'string' ? current.replace(/^#/, '').trim() : current;
            const parsed = parseJSONSafe(cleaned);
            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch { return {}; }
    };

    const loadFilterToForm = () => {
        loadFilterableKeys();

        const current = typeof getCurrentFilter === 'function' ? getCurrentFilter() : getCurrentFilter;
        const cleaned = current ? (typeof current === 'string' ? current.replace(/^#/, '').trim() : current) : '';
        const parsed = cleaned ? parseJSONSafe(cleaned) : null;

        // Detect if this is a plain string filter (not JSON)
        if (cleaned && (!parsed || typeof parsed !== 'object')) {
            setIsStringFilter(true);
            setIntegrationFilters([]);
            setValue('filterConditions', '');
            setValue('dependentResources', []);
            return;
        }

        setIsStringFilter(false);
        const obj = parsed || {};
        const detectedKey = conditionsKeyOptions.find(k => obj[k] !== undefined) || 'conditions';
        setConditionsKey(detectedKey);
        const conditionsValue = obj[detectedKey] || '';
        setValue('filterConditions', typeof conditionsValue === 'string' ? conditionsValue : JSON.stringify(conditionsValue, null, 2));
        // postFilters is the preferred key; fall back to the legacy integrationFilters for older saved configs
        const loadedPostFilters = Array.isArray(obj.postFilters) ? obj.postFilters
            : (Array.isArray(obj.integrationFilters) ? obj.integrationFilters : []);
        setIntegrationFilters(loadedPostFilters.map(f => ({
            op: f.op || 'in',
            filterKey: f.filterKey || '',
            subKey: f.subKey || '',
            source_resource_id: f.source_resource_id || '',
            // referenceKind: 'migration' (staged records) | 'valueMapping' (approved setup value mappings)
            referenceKind: f.referenceKind || 'migration',
            valueSide: f.valueSide || 'source',
            key: f.key || 'source_rec_id',
            integerValues: !!f.integerValues,
            // A filter with no source_resource_id but a value is a static-value filter
            useStaticValue: !f.source_resource_id && f.value !== undefined,
            // Keep as an array for CreatableMultiSelect; wrap a lone scalar
            value: Array.isArray(f.value) ? f.value : (f.value !== undefined && f.value !== '' ? [f.value] : []),
        })));
        setGlobalFilters(Array.isArray(obj.globalFilters) ? obj.globalFilters.map(f => ({
            field: f.field || '',
            op: f.op || '=',
            // Keep falsy-but-meaningful values (false, 0); only absent/empty → ''
            value: f.value === undefined || f.value === null ? '' : f.value,
            useReference: !!f.source_resource_id,
            source_resource_id: f.source_resource_id || '',
            // referenceKind: 'migration' (staged records) | 'valueMapping' (approved setup value mappings)
            referenceKind: f.referenceKind || 'migration',
            valueSide: f.valueSide || 'source',
            key: f.key || 'source_rec_id',
            integerValues: !!f.integerValues,
        })) : []);
        const argsObj = obj.args || {};
        setApiArgs(Object.entries(argsObj).map(([key, value]) => ({ key, value: typeof value === 'object' ? JSON.stringify(value) : String(value) })));
        setFields(Array.isArray(obj.fields) ? obj.fields.join(', ') : (obj.fields || ''));
        setOptions(Array.isArray(obj.options) ? obj.options : (obj.options ? [obj.options] : []));
        setValue('dependentResources', Array.isArray(obj.dependentResources) ? obj.dependentResources.map(Number) : []);
    };

    const addIntegrationFilter = () => setIntegrationFilters(prev => [
        ...prev, { op: 'in', filterKey: '', subKey: '', source_resource_id: '', referenceKind: 'migration', valueSide: 'source', key: 'source_rec_id', integerValues: false, useStaticValue: false, value: [] }
    ]);

    const updateIntegrationFilter = (index, field, value) => {
        setIntegrationFilters(prev => prev.map((f, i) => {
            if (i !== index) return f;
            const updated = { ...f, [field]: value };
            // Array operators always work against a static value list — force static-value mode on
            if (field === 'op' && STATIC_VALUE_OPS.includes(value)) {
                updated.useStaticValue = true;
            }
            return updated;
        }));
    };

    const removeIntegrationFilter = (index) => setIntegrationFilters(prev => prev.filter((_, i) => i !== index));

    const addGlobalFilter = () => setGlobalFilters(prev => [...prev, { field: '', op: '=', value: '', useReference: false, source_resource_id: '', referenceKind: 'migration', valueSide: 'source', key: 'source_rec_id', integerValues: false }]);

    const updateGlobalFilter = (index, field, value) => {
        setGlobalFilters(prev => prev.map((f, i) => {
            if (i !== index) return f;
            const updated = { ...f, [field]: value };
            // Reset op and value when field changes
            if (field === 'field') {
                updated.op = '=';
                updated.value = '';
            }
            return updated;
        }));
    };

    const removeGlobalFilter = (index) => setGlobalFilters(prev => prev.filter((_, i) => i !== index));

    const addApiArg = () => setApiArgs(prev => [...prev, { key: '', value: '' }]);
    const updateApiArg = (index, field, value) => setApiArgs(prev => prev.map((a, i) => i === index ? { ...a, [field]: value } : a));
    const removeApiArg = (index) => setApiArgs(prev => prev.filter((_, i) => i !== index));

    const onSubmit = (formData) => {
        try {
            const result = {};

            if (conditionsKey === 'globalFilters') {
                // Global filters are built from the UI rows, not the textarea
                if (globalFilters.length > 0) {
                    const parseGlobalValue = (val) => {
                        if (val === 'true') return true;
                        if (val === 'false') return false;
                        if (val !== '' && !isNaN(val) && !isNaN(parseFloat(val))) return Number(val);
                        try { const p = JSON.parse(val); if (Array.isArray(p)) return p; } catch {}
                        return val;
                    };
                    const built = globalFilters.filter(f => f.field).map(f => {
                        const keyMeta = getKeyMeta(f.field);
                        const filter = { field: f.field, op: f.op || '=' };

                        // If using resource reference, include source_resource_id instead of static value.
                        // value is resolved at runtime by the stager.
                        if (f.useReference && f.source_resource_id) {
                            filter.source_resource_id = Number(f.source_resource_id);
                            if (f.referenceKind === 'valueMapping') {
                                // Resolve from this job's Approved setup value mappings (match on value_id)
                                filter.referenceKind = 'valueMapping';
                                filter.valueSide = f.valueSide === 'destination' ? 'destination' : 'source';
                            } else {
                                // Resolve from staged migration records
                                filter.key = f.key || 'source_rec_id';
                                if (f.integerValues) filter.integerValues = true;
                            }
                        } else {
                            const parsedValue = parseGlobalValue(f.value);
                            // For in/not in operators, ensure value is an array
                            let finalValue = parsedValue;
                            if ((f.op === 'in' || f.op === 'not in') && !Array.isArray(parsedValue)) {
                                finalValue = String(parsedValue).split(',').map(v => v.trim()).filter(Boolean);
                                // Convert to numbers if they look numeric
                                if (finalValue.every(v => v !== '' && !isNaN(v))) {
                                    finalValue = finalValue.map(Number);
                                }
                            }
                            filter.value = finalValue;
                        }

                        // Include filter_param (e.g. childConditions: true) if defined
                        if (keyMeta?.filter_param) filter[keyMeta.filter_param] = true;
                        return filter;
                    });
                    if (built.length > 0) result.globalFilters = built;
                }
            } else {
                const rawConditions = formData.filterConditions?.trim();
                if (rawConditions) {
                    const parsed = parseJSONSafe(rawConditions);
                    result[conditionsKey] = (parsed && typeof parsed === 'object') ? parsed : rawConditions;
                }
            }

            if (integrationFilters.length > 0) {
                // CreatableMultiSelect already returns a typed array (int/string coerced).
                // Accept a comma-string too for filters loaded from older saves not yet re-edited.
                const parseValueList = (raw) => {
                    if (Array.isArray(raw)) return raw;
                    const parts = String(raw).split(',').map(v => v.trim()).filter(v => v !== '');
                    if (parts.length === 0) return [];
                    const allNumeric = parts.every(v => !isNaN(v) && !isNaN(parseFloat(v)));
                    return allNumeric ? parts.map(Number) : parts;
                };

                const hasStaticValue = (f) => Array.isArray(f.value) ? f.value.length > 0 : String(f.value ?? '').trim() !== '';

                // postFilters is the preferred key; integrationFilters is the legacy synonym
                result.postFilters = integrationFilters
                    .filter(f => {
                        const isStatic = f.useStaticValue || STATIC_VALUE_OPS.includes(f.op);
                        // Static filters need a filterKey and a value; reference filters need a filterKey and a source resource
                        return isStatic
                            ? (f.filterKey && hasStaticValue(f))
                            : (f.filterKey && f.source_resource_id);
                    })
                    .map(f => {
                        const isStatic = f.useStaticValue || STATIC_VALUE_OPS.includes(f.op);
                        if (isStatic) {
                            const filter = {
                                op: f.op || 'in',
                                filterKey: f.filterKey,
                                value: parseValueList(f.value),
                            };
                            // subKey drills into array-of-object fields (e.g. types[].name)
                            if (f.subKey && String(f.subKey).trim() !== '') filter.subKey = f.subKey.trim();
                            return filter;
                        }
                        // Reference mode. value is resolved at runtime by the stager.
                        const refFilter = {
                            op: f.op || 'in',
                            filterKey: f.filterKey,
                            source_resource_id: Number(f.source_resource_id),
                        };
                        if (f.referenceKind === 'valueMapping') {
                            refFilter.referenceKind = 'valueMapping';
                            refFilter.valueSide = f.valueSide === 'destination' ? 'destination' : 'source';
                        } else {
                            refFilter.key = f.key || 'source_rec_id';
                            if (f.integerValues) refFilter.integerValues = true;
                        }
                        return refFilter;
                    });
                if (result.postFilters.length === 0) delete result.postFilters;
            }

            const deps = formData.dependentResources;
            const validDeps = (Array.isArray(deps) ? deps : []).map(Number).filter(n => !isNaN(n) && n > 0);
            if (validDeps.length > 0) result.dependentResources = validDeps;

            // API Args
            const validArgs = apiArgs.filter(a => a.key.trim());
            if (validArgs.length > 0) {
                result.args = {};
                for (const arg of validArgs) {
                    let parsedValue = arg.value;
                    try { const p = JSON.parse(arg.value); parsedValue = p; } catch {}
                    result.args[arg.key.trim()] = parsedValue;
                }
            }

            // Fields
            const trimmedFields = fields.trim();
            if (trimmedFields) {
                result.fields = trimmedFields.split(',').map(f => f.trim()).filter(Boolean);
            }

            // Options
            if (options.length > 0) {
                result.options = options;
            }

            onApply?.(result);
        } catch (e) { console.warn('[FilterBuilder] Submit error:', e); }
    };

    // Render the value input for a global filter row based on field data type
    const renderGlobalFilterValue = (filter, i) => {
        const keyMeta = getKeyMeta(filter.field);

        if (keyMeta?.data_type === 'boolean') {
            return (
                <div className='react-select-sm'>
                    <Select
                        className='react-select-container'
                        classNamePrefix='react-select'
                        options={BOOLEAN_OPTIONS}
                        // filter.value may be a real boolean (loaded JSON) or 'true'/'false' string (just picked)
                        value={BOOLEAN_OPTIONS.find(o => o.value === String(filter.value)) || null}
                        onChange={(opt) => updateGlobalFilter(i, 'value', opt?.value || '')}
                        isDisabled={readOnly}
                        placeholder='Select...'
                        isClearable
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        menuPosition='fixed'
                    />
                </div>
            );
        }

        // In reference mode the value is resolved at runtime from the referenced source
        // (configured on the second row below), so the value cell just shows a hint.
        if (filter.useReference && (filter.op === 'in' || filter.op === 'not in')) {
            return (
                <Input bsSize='sm' value='Resolved from reference ↓' readOnly disabled
                    style={{ fontSize: 11, fontStyle: 'italic', color: '#888' }} />
            );
        }

        // For in/not in operators, use CreatableMultiSelect for tag-style input
        if (filter.op === 'in' || filter.op === 'not in') {
            const currentValues = Array.isArray(filter.value) ? filter.value
                : (typeof filter.value === 'string' && filter.value ? filter.value.split(',').map(v => v.trim()).filter(Boolean) : []);
            return (
                <div className='react-select-sm'>
                    <CreatableMultiSelect
                        value={currentValues}
                        onChange={(values) => updateGlobalFilter(i, 'value', values)}
                        placeholder='Type and press Enter...'
                        disabled={readOnly}
                        size='sm'
                    />
                </div>
            );
        }

        return (
            <Input bsSize='sm'
                placeholder='Value...'
                value={typeof filter.value === 'object' ? JSON.stringify(filter.value) : filter.value}
                readOnly={readOnly}
                onChange={(e) => updateGlobalFilter(i, 'value', e.target.value)}
            />
        );
    };

    // Second-line reference configuration for a global filter row (shown when Ref is on).
    const renderGlobalFilterReference = (filter, i) => (
        <div className='d-flex align-items-center flex-wrap' style={{ gap: 8, padding: '6px 8px', background: 'rgba(0,0,0,0.03)', borderRadius: 4 }}>
            {/* Source resource to resolve values from */}
            <div className='react-select-sm' style={{ flex: '1 1 220px', minWidth: 180 }}>
                <Select
                    className='react-select-container'
                    classNamePrefix='react-select'
                    options={resourceOptions}
                    value={resourceOptions.find(o => String(o.value) === String(filter.source_resource_id)) || null}
                    onChange={(opt) => updateGlobalFilter(i, 'source_resource_id', opt?.value || '')}
                    isDisabled={readOnly}
                    placeholder='Source Resource...'
                    isClearable
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition='fixed'
                />
            </div>

            {/* Reference kind: selected = solid primary; unselected = muted ghost (just info) */}
            <div className='btn-group btn-group-sm' role='group'>
                <button type='button'
                    className={`btn btn-sm ${filter.referenceKind !== 'valueMapping' ? 'btn-primary' : 'btn-outline-light text-muted'}`}
                    style={{ fontSize: 10, padding: '2px 8px' }}
                    disabled={readOnly}
                    title="Resolve from this job's staged migration records"
                    onClick={() => updateGlobalFilter(i, 'referenceKind', 'migration')}>Records</button>
                <button type='button'
                    className={`btn btn-sm ${filter.referenceKind === 'valueMapping' ? 'btn-primary' : 'btn-outline-light text-muted'}`}
                    style={{ fontSize: 10, padding: '2px 8px' }}
                    disabled={readOnly}
                    title="Resolve from this job's Approved setup value mappings (value IDs)"
                    onClick={() => updateGlobalFilter(i, 'referenceKind', 'valueMapping')}>Value Mappings</button>
            </div>

            {filter.referenceKind === 'valueMapping' ? (
                <div className='btn-group btn-group-sm' role='group'>
                    <button type='button'
                        className={`btn btn-sm ${filter.valueSide !== 'destination' ? 'btn-primary' : 'btn-outline-light text-muted'}`}
                        style={{ fontSize: 10, padding: '2px 8px' }}
                        disabled={readOnly}
                        title='Use source-side value IDs'
                        onClick={() => updateGlobalFilter(i, 'valueSide', 'source')}>Source IDs</button>
                    <button type='button'
                        className={`btn btn-sm ${filter.valueSide === 'destination' ? 'btn-primary' : 'btn-outline-light text-muted'}`}
                        style={{ fontSize: 10, padding: '2px 8px' }}
                        disabled={readOnly}
                        title='Use destination-side value IDs'
                        onClick={() => updateGlobalFilter(i, 'valueSide', 'destination')}>Dest IDs</button>
                </div>
            ) : (
                <Input bsSize='sm' placeholder='source_rec_id or $.resource_data/key' value={filter.key || 'source_rec_id'} readOnly={readOnly}
                    style={{ height: 30, fontSize: 11, flex: '1 1 200px', minWidth: 160 }}
                    onChange={(e) => updateGlobalFilter(i, 'key', e.target.value)} />
            )}
        </div>
    );

    return (
        <ModalButtonBuilder
            title='Filter Builder'
            type='button'
            className='btn-info btn-sm'
            buttonStyle={{ minWidth: 110 }}
            modalTitle='Resource Filter Builder'
            formId='filter-builder-form'
            style={{ width: 700, maxWidth: 700 }}
            onSubmit={readOnly || isStringFilter ? false : 'Apply Filter'}
            onCancelLabel={readOnly || isStringFilter ? 'Close' : 'Cancel'}
            onClick={loadFilterToForm}
            body={
                <Form id='filter-builder-form' onSubmit={(e) => { e.stopPropagation(); handleSubmit((data) => {
                    onSubmit(data);
                    ToastBuilder('info', "Don't forget to Submit to save", {}, 5000);
                })(e); }}>

                    {isStringFilter && (
                        <div className='alert alert-warning' style={{ fontSize: 12, padding: '8px 12px', marginBottom: 10 }}>
                            This resource uses a string-based filter (not JSON). The Filter Builder only supports JSON filters. Edit the filter directly in the textarea instead.
                        </div>
                    )}

                    {/* Conditions / Search Filters / Global Filters */}
                    <div className='section-group-muted' style={{ borderRadius: 6, padding: '10px 14px' }}>
                        <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: conditionsKey === 'globalFilters' ? 6 : -12 }}>
                            <span className='section-group-title' style={{ fontWeight: 600, fontSize: 13 }}>
                                {conditionsKey === 'conditions' ? 'Conditions' : conditionsKey === 'searchFilters' ? 'Search Filters' : 'Global Filters'}
                            </span>
                            <div className='d-flex align-items-center gap-2'>
                                {conditionsKey === 'globalFilters' && !readOnly && <ButtonBuilder label='+ Add' type='button' className='btn-outline-secondary btn-sm' useSpinner={false} onClick={addGlobalFilter} />}
                                <Select
                                    className='react-select-container'
                                    classNamePrefix='react-select'
                                    value={conditionsKeySelectOptions.find(o => o.value === conditionsKey)}
                                    options={conditionsKeySelectOptions}
                                    isDisabled={readOnly}
                                    onChange={(opt) => setConditionsKey(opt.value)}
                                    styles={{ ...selectStyles, control: (base) => ({ ...base, minWidth: 160 }) }}
                                    menuPortalTarget={document.body}
                                    menuPosition='fixed'
                                />
                            </div>
                        </div>
                        {conditionsKey !== 'globalFilters' ? (
                            <RegisteredInputsBuilder
                                control={control}
                                registrator={register}
                                registratorErrors={errors}
                                readOnly={readOnly}
                                inputs={[{
                                    label: null, labelSize: 0, inputSize: 12, size: 12,
                                    type: 'textarea', name: 'filterConditions',
                                    placeholder: conditionsKey === 'searchFilters' ? '[{"op": "exist", "field": "id"}]' : "status/name LIKE 'Active%'",
                                    style: { height: 80, fontSize: 12, fontFamily: 'monospace' },
                                    settings: { handleTabs: true, resize: true, compact: true },
                                }]}
                            />
                        ) : (
                            <>
                                {globalFilters.map((filter, i) => {
                                    const operators = getOperatorsForField(filter.field);
                                    const canReference = filter.op === 'in' || filter.op === 'not in';
                                    return (
                                      <div key={i} className='mb-2'>
                                        <Row className='align-items-start' style={{ flexWrap: 'nowrap', gap: 0 }}>
                                            <Col style={{ flex: '0 0 230px', paddingRight: 4, paddingLeft: 6 }}><label style={{ fontSize: 12 }}>Field</label>
                                                {hasKeys ? (
                                                    <div className='react-select-sm'>
                                                        {/* Creatable so a field not in the known keys can still be typed (e.g. an API field we didn't register) */}
                                                        <CreatableSelect
                                                            className='react-select-container'
                                                            classNamePrefix='react-select'
                                                            options={keyOptions}
                                                            value={keyOptions.find(o => o.value === filter.field) || (filter.field ? { value: filter.field, label: filter.field } : null)}
                                                            onChange={(opt) => updateGlobalFilter(i, 'field', opt?.value || '')}
                                                            isDisabled={readOnly}
                                                            placeholder='Select or type field...'
                                                            formatCreateLabel={(input) => `Use "${input}"`}
                                                            isClearable
                                                            styles={selectStyles}
                                                            menuPortalTarget={document.body}
                                                            menuPosition='fixed'
                                                        />
                                                    </div>
                                                ) : (
                                                    <Input bsSize='sm' placeholder='board/id' value={filter.field} readOnly={readOnly}
                                                        onChange={(e) => updateGlobalFilter(i, 'field', e.target.value)} />
                                                )}
                                            </Col>
                                            <Col style={{ flex: '0 0 90px', paddingRight: 4, paddingLeft: 4 }}>
                                                <label style={{ fontSize: 12 }}>Op</label>
                                                <div className='react-select-sm'>
                                                <Select
                                                    className='react-select-container'
                                                    classNamePrefix='react-select'
                                                    value={operators.find(o => o.value === filter.op) || null}
                                                    options={operators}
                                                    isDisabled={readOnly}
                                                    onChange={(opt) => updateGlobalFilter(i, 'op', opt?.value || '=')}
                                                    styles={selectStyles}
                                                    menuPortalTarget={document.body}
                                                    menuPosition='fixed'
                                                />
                                                </div>
                                            </Col>
                                            <Col style={{ flex: '1 1 0', minWidth: 0, paddingLeft: 4, paddingRight: 4 }}><label style={{ fontSize: 12 }}>Value</label>
                                                {renderGlobalFilterValue(filter, i)}
                                            </Col>
                                            <Col className='d-flex align-items-start gap-1' style={{ paddingLeft: 2, paddingRight: 0, flex: '0 0 30px' }}>
                                                <div style={{ marginTop: 29 }}>
                                                {!readOnly && <Button size='sm' color='link' className='text-danger p-0' onClick={() => removeGlobalFilter(i)}>✕</Button>}
                                                </div>
                                            </Col>
                                        </Row>
                                        {canReference && !readOnly && (
                                            <div className='d-flex align-items-center' style={{ marginTop: 4, marginLeft: 6, gap: 12 }}>
                                                <button
                                                    type='button'
                                                    className={`btn btn-sm ${filter.useReference ? 'btn-success' : 'btn-outline-secondary'}`}
                                                    style={{ fontSize: 10, padding: '2px 8px', whiteSpace: 'nowrap' }}
                                                    onClick={() => updateGlobalFilter(i, 'useReference', !filter.useReference)}
                                                    title={filter.useReference ? 'Switch to manual values' : 'Resolve values from another resource'}
                                                >
                                                    {filter.useReference ? 'Use Reference ✓' : 'Use Reference'}
                                                </button>
                                                {/* Int only applies to the Records kind; value mappings always match on value_id */}
                                                {filter.useReference && filter.referenceKind !== 'valueMapping' && (
                                                    <div className='form-check d-flex align-items-center' style={{ gap: 4 }}>
                                                        <input className='form-check-input' type='checkbox' id={`gf-int-${i}`} style={{ marginTop: 0 }}
                                                            checked={!!filter.integerValues}
                                                            onChange={(e) => updateGlobalFilter(i, 'integerValues', e.target.checked)} />
                                                        <label className='form-check-label' htmlFor={`gf-int-${i}`} style={{ fontSize: 11, cursor: 'pointer', marginBottom: 0 }}>Integer values</label>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {canReference && filter.useReference && (
                                            <div style={{ marginTop: 4, marginLeft: 6, marginRight: 6 }}>
                                                {renderGlobalFilterReference(filter, i)}
                                            </div>
                                        )}
                                      </div>
                                    );
                                })}
                                {globalFilters.length === 0 && <p style={{ fontSize: 12, color: '#888', margin: '4px 0' }}>No global filters — click + Add to create one</p>}
                            </>
                        )}
                    </div>

                    {/* Post Fetch Filters */}
                    <div className='section-group-accent' style={{ borderRadius: 6, padding: '10px 14px', marginTop: 10 }}>
                        <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: 6 }}>
                            <span className='section-group-title' style={{ fontWeight: 600, fontSize: 13 }}>Post Fetch Filters</span>
                            {!readOnly && <ButtonBuilder label='+ Add' type='button' className='btn-outline-secondary btn-sm' useSpinner={false} onClick={addIntegrationFilter} />}
                        </div>
                        {integrationFilters.map((filter, i) => (
                            <div key={i} className='mb-3 p-2 filter-card' style={{ borderRadius: 4 }}>
                                <Row className='mb-1 align-items-center'>
                                    <Col md={5}><label style={{ fontSize: 12 }}>Filter Key (API field)</label>
                                        {hasKeys ? (
                                            <div className='react-select-sm'>
                                                {/* Creatable so users can type a field not in the known keys —
                                                    e.g. an array field like `types` (only `types/name` is registered) */}
                                                <CreatableSelect
                                                    className='react-select-container'
                                                    classNamePrefix='react-select'
                                                    options={keyOptions}
                                                    value={keyOptions.find(o => o.value === filter.filterKey) || (filter.filterKey ? { value: filter.filterKey, label: filter.filterKey } : null)}
                                                    onChange={(opt) => updateIntegrationFilter(i, 'filterKey', opt?.value || '')}
                                                    isDisabled={readOnly}
                                                    placeholder='Select or type field...'
                                                    formatCreateLabel={(input) => `Use "${input}"`}
                                                    isClearable
                                                    styles={selectStyles}
                                                    menuPortalTarget={document.body}
                                                    menuPosition='fixed'
                                                />
                                            </div>
                                        ) : (
                                            <Input bsSize='sm' placeholder='company/id' value={filter.filterKey} readOnly={readOnly}
                                                onChange={(e) => updateIntegrationFilter(i, 'filterKey', e.target.value)} />
                                        )}
                                    </Col>
                                    <Col md={3}><label style={{ fontSize: 12 }}>Operation</label>
                                        <div className='react-select-sm'>
                                        <Select
                                            className='react-select-container'
                                            classNamePrefix='react-select'
                                            value={opOptions.find(o => o.value === filter.op)}
                                            options={opOptions}
                                            isDisabled={readOnly}
                                            onChange={(opt) => updateIntegrationFilter(i, 'op', opt.value)}
                                            styles={selectStyles}
                                            menuPortalTarget={document.body}
                                            menuPosition='fixed'
                                        />
                                        </div>
                                    </Col>
                                    <Col md={3} className='d-flex align-items-end' style={{ paddingTop: 20 }}>
                                        {/* Static-value mode toggle — array operators force it on and lock it */}
                                        <div className='form-check d-flex align-items-center' style={{ gap: 6, marginBottom: 7 }}>
                                            <input className='form-check-input' type='checkbox' id={`staticVal-${i}`} style={{ marginTop: 0 }}
                                                checked={filter.useStaticValue || STATIC_VALUE_OPS.includes(filter.op)}
                                                disabled={readOnly || STATIC_VALUE_OPS.includes(filter.op)}
                                                onChange={(e) => updateIntegrationFilter(i, 'useStaticValue', e.target.checked)} />
                                            <label className='form-check-label' htmlFor={`staticVal-${i}`} style={{ fontSize: 12, cursor: 'pointer', marginBottom: 0 }}
                                                title='Filter against explicit values instead of IDs from another staged resource'>Static Values</label>
                                        </div>
                                    </Col>
                                    <Col md={1} className='d-flex align-items-end'>
                                        {!readOnly && <Button size='sm' color='link' className='text-danger p-0' onClick={() => removeIntegrationFilter(i)}>✕</Button>}
                                    </Col>
                                </Row>
                                {(filter.useStaticValue || STATIC_VALUE_OPS.includes(filter.op)) ? (
                                    <Row className='mt-1 align-items-start'>
                                        {/* subKey is only meaningful for array operators that drill into array-of-object fields */}
                                        {STATIC_VALUE_OPS.includes(filter.op) && (
                                            <Col md={4}><label style={{ fontSize: 12 }}>Array Sub-Key</label>
                                                <Input bsSize='sm' placeholder='name' value={filter.subKey || ''} readOnly={readOnly}
                                                    onChange={(e) => updateIntegrationFilter(i, 'subKey', e.target.value)} />
                                                <p style={{ fontSize: 10, color: '#888', margin: '2px 0 0' }}>e.g. filterKey <code>types</code> + subKey <code>name</code> → checks each <code>types[].name</code></p>
                                            </Col>
                                        )}
                                        <Col md={STATIC_VALUE_OPS.includes(filter.op) ? 8 : 12}><label style={{ fontSize: 12 }}>Values</label>
                                            <div className='react-select-sm'>
                                                <CreatableMultiSelect
                                                    value={Array.isArray(filter.value) ? filter.value
                                                        : (typeof filter.value === 'string' && filter.value ? filter.value.split(',').map(v => v.trim()).filter(Boolean) : [])}
                                                    onChange={(values) => updateIntegrationFilter(i, 'value', values)}
                                                    placeholder='Type a value and press Enter...'
                                                    disabled={readOnly}
                                                    size='sm'
                                                />
                                            </div>
                                            <p style={{ fontSize: 10, color: '#888', margin: '2px 0 0' }}>Numeric values become integers; wrap in quotes to force string (e.g. "500").</p>
                                        </Col>
                                    </Row>
                                ) : (
                                    <Row className='mt-1 align-items-start'>
                                        <Col md={5}><label style={{ fontSize: 12 }}>Source Resource</label>
                                            <div className='react-select-sm'>
                                            <Select
                                                className='react-select-container'
                                                classNamePrefix='react-select'
                                                value={resourceOptions.find(o => String(o.value) === String(filter.source_resource_id)) || null}
                                                options={resourceOptions}
                                                isDisabled={readOnly}
                                                onChange={(opt) => updateIntegrationFilter(i, 'source_resource_id', opt?.value || '')}
                                                placeholder='Select...'
                                                isClearable
                                                styles={selectStyles}
                                                menuPortalTarget={document.body}
                                                menuPosition='fixed'
                                            />
                                            </div>
                                            {/* Reference kind: selected = solid primary; unselected = muted ghost */}
                                            <div className='btn-group btn-group-sm mt-1' role='group'>
                                                <button type='button'
                                                    className={`btn btn-sm ${filter.referenceKind !== 'valueMapping' ? 'btn-primary' : 'btn-outline-light text-muted'}`}
                                                    style={{ fontSize: 10, padding: '2px 8px' }}
                                                    disabled={readOnly}
                                                    title="Resolve from this job's staged migration records"
                                                    onClick={() => updateIntegrationFilter(i, 'referenceKind', 'migration')}>Records</button>
                                                <button type='button'
                                                    className={`btn btn-sm ${filter.referenceKind === 'valueMapping' ? 'btn-primary' : 'btn-outline-light text-muted'}`}
                                                    style={{ fontSize: 10, padding: '2px 8px' }}
                                                    disabled={readOnly}
                                                    title="Resolve from this job's Approved setup value mappings (value IDs)"
                                                    onClick={() => updateIntegrationFilter(i, 'referenceKind', 'valueMapping')}>Value Mappings</button>
                                            </div>
                                        </Col>
                                        {filter.referenceKind === 'valueMapping' ? (
                                            <Col md={7}><label style={{ fontSize: 12 }}>Value IDs</label>
                                                <div className='btn-group btn-group-sm d-block' role='group'>
                                                    <button type='button'
                                                        className={`btn btn-sm ${filter.valueSide !== 'destination' ? 'btn-primary' : 'btn-outline-light text-muted'}`}
                                                        style={{ fontSize: 10, padding: '2px 8px' }}
                                                        disabled={readOnly}
                                                        title='Use source-side value IDs'
                                                        onClick={() => updateIntegrationFilter(i, 'valueSide', 'source')}>Source IDs</button>
                                                    <button type='button'
                                                        className={`btn btn-sm ${filter.valueSide === 'destination' ? 'btn-primary' : 'btn-outline-light text-muted'}`}
                                                        style={{ fontSize: 10, padding: '2px 8px' }}
                                                        disabled={readOnly}
                                                        title='Use destination-side value IDs'
                                                        onClick={() => updateIntegrationFilter(i, 'valueSide', 'destination')}>Dest IDs</button>
                                                </div>
                                            </Col>
                                        ) : (
                                            <>
                                                <Col md={4}><label style={{ fontSize: 12 }}>Record Key</label>
                                                    <Input bsSize='sm' placeholder='source_rec_id' value={filter.key} readOnly={readOnly}
                                                        onChange={(e) => updateIntegrationFilter(i, 'key', e.target.value)} />
                                                </Col>
                                                <Col md={3} className='d-flex align-items-end' style={{ paddingBottom: 2 }}>
                                                    <div className='form-check d-flex align-items-center' style={{ gap: 6 }}>
                                                        <input className='form-check-input' type='checkbox' id={`intVal-${i}`} style={{ marginTop: 0 }}
                                                            checked={!!filter.integerValues} disabled={readOnly}
                                                            onChange={(e) => updateIntegrationFilter(i, 'integerValues', e.target.checked)} />
                                                        <label className='form-check-label' htmlFor={`intVal-${i}`} style={{ fontSize: 12, cursor: 'pointer', marginBottom: 0 }}>Integer Values</label>
                                                    </div>
                                                </Col>
                                            </>
                                        )}
                                    </Row>
                                )}
                            </div>
                        ))}
                        {integrationFilters.length === 0 && <p style={{ fontSize: 12, color: '#888', margin: '4px 0' }}>No post fetch filters</p>}
                    </div>

                    {/* API Args */}
                    <div className='section-group-muted' style={{ borderRadius: 6, padding: '10px 14px', marginTop: 10 }}>
                        <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: 6 }}>
                            <span className='section-group-title' style={{ fontWeight: 600, fontSize: 13 }}>API Arguments</span>
                            {!readOnly && <ButtonBuilder label='+ Add' type='button' className='btn-outline-secondary btn-sm' useSpinner={false} onClick={addApiArg} />}
                        </div>
                        {apiArgs.map((arg, i) => (
                            <Row key={i} className='mb-1 align-items-center' style={{ flexWrap: 'nowrap', gap: 0 }}>
                                <Col style={{ flex: '0 0 160px', paddingRight: 4, paddingLeft: 6 }}>
                                    <Input bsSize='sm' placeholder='Key (e.g., include)' value={arg.key} readOnly={readOnly}
                                        onChange={(e) => updateApiArg(i, 'key', e.target.value)} />
                                </Col>
                                <Col style={{ flex: '1 1 0', minWidth: 0, paddingLeft: 4, paddingRight: 4 }}>
                                    <Input bsSize='sm' placeholder='Value (e.g., requester)' value={arg.value} readOnly={readOnly}
                                        onChange={(e) => updateApiArg(i, 'value', e.target.value)} />
                                </Col>
                                <Col className='d-flex align-items-center' style={{ paddingLeft: 0, flex: '0 0 24px' }}>
                                    {!readOnly && <Button size='sm' color='link' className='text-danger p-0' onClick={() => removeApiArg(i)}>✕</Button>}
                                </Col>
                            </Row>
                        ))}
                        {apiArgs.length === 0 && <p style={{ fontSize: 12, color: '#888', margin: '4px 0' }}>No extra API arguments</p>}
                    </div>

                    {/* Fields */}
                    <div className='section-group-muted' style={{ borderRadius: 6, padding: '10px 14px', marginTop: 10 }}>
                        <span className='section-group-title' style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 6 }}>Fields</span>
                        <Input placeholder='Comma-separated field names (blank = all fields)' value={fields} readOnly={readOnly}
                            onChange={(e) => setFields(e.target.value)} />
                        <p style={{ fontSize: 11, color: '#888', margin: '4px 0 0' }}>Leave empty to fetch all fields. The factory translates this per integration.</p>
                    </div>

                    {/* Options */}
                    <div className='section-group-muted' style={{ borderRadius: 6, padding: '10px 14px', marginTop: 10 }}>
                        <span className='section-group-title' style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 6 }}>Options</span>
                        <Select
                            className='react-select-container'
                            classNamePrefix='react-select'
                            isMulti
                            value={options.map(o => ({ label: o, value: o }))}
                            options={[
                                { label: 'Clean Data (CWM: removes _info and href)', value: 'cleanData' },
                            ]}
                            isDisabled={readOnly}
                            onChange={(selected) => setOptions((selected || []).map(s => s.value))}
                            placeholder='Select options...'
                            isClearable
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                            menuPosition='fixed'
                        />
                    </div>

                    {/* Dependent Resources */}
                    <div className='section-group-muted' style={{ borderRadius: 6, padding: '10px 14px', marginTop: 10 }}>
                        <span className='section-group-title' style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: -12 }}>Dependent Resources</span>
                        <RegisteredInputsBuilder
                            control={control}
                            registrator={register}
                            registratorErrors={errors}
                            readOnly={readOnly}
                            inputs={[{
                                label: null, labelSize: 0, inputSize: 12, size: 12,
                                type: 'multiselect', name: 'dependentResources',
                                placeholder: 'Select dependent resources...',
                                options: resourceOptions,
                                settings: { compact: true },
                            }]}
                        />
                    </div>
                </Form>
            }
        />
    );
};

export default FilterBuilder;
